// Mobile port of the web Task Configuration → Values editor. Reads/writes
// user_settings.value_settings and user_settings.unscheduled_task_priority_
// bonus directly (see src/interfaces/settings.ts ValueSetting/Criterion on
// web) — same shape, same source of truth as the web app.
import React from 'react';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface Criterion {
  id: string;
  title: string;
  score: number;
}

interface ValueSetting {
  id: string;
  isActive: boolean;
  name: string;
  description?: string;
  criteria: Criterion[];
}

export default function ValuesEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const {
    value: bonus,
    setValue: setBonus,
    loading: bonusLoading,
  } = useSettingColumn<number>(userId, 'unscheduled_task_priority_bonus', 0);
  const {
    value: values,
    setValue: setValues,
    loading: valuesLoading,
  } = useSettingColumn<ValueSetting[]>(userId, 'value_settings', []);

  const updateValue = (id: string, patch: Partial<ValueSetting>) => {
    setValues(values.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  };

  const deleteValue = (id: string) => {
    Alert.alert('Delete value?', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => setValues(values.filter((v) => v.id !== id)),
      },
    ]);
  };

  const addValue = () => {
    const newValue: ValueSetting = {
      id: `value-${Date.now()}`,
      isActive: true,
      name: 'New Value',
      description: '',
      criteria: [],
    };
    setValues([...values, newValue]);
  };

  const moveValue = (id: string, direction: -1 | 1) => {
    const idx = values.findIndex((v) => v.id === id);
    const swapIdx = idx + direction;
    if (idx === -1 || swapIdx < 0 || swapIdx >= values.length) return;
    const next = [...values];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setValues(next);
  };

  const addCriterion = (value: ValueSetting) => {
    const criterion: Criterion = { id: `criterion-${Date.now()}`, title: 'New Criterion', score: 0 };
    updateValue(value.id, { criteria: [...value.criteria, criterion] });
  };

  const updateCriterion = (value: ValueSetting, criterionId: string, patch: Partial<Criterion>) => {
    updateValue(value.id, {
      criteria: value.criteria.map((c) => (c.id === criterionId ? { ...c, ...patch } : c)),
    });
  };

  const deleteCriterion = (value: ValueSetting, criterionId: string) => {
    updateValue(value.id, { criteria: value.criteria.filter((c) => c.id !== criterionId) });
  };

  const moveCriterion = (value: ValueSetting, criterionId: string, direction: -1 | 1) => {
    const idx = value.criteria.findIndex((c) => c.id === criterionId);
    const swapIdx = idx + direction;
    if (idx === -1 || swapIdx < 0 || swapIdx >= value.criteria.length) return;
    const next = [...value.criteria];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    updateValue(value.id, { criteria: next });
  };

  const loading = bonusLoading || valuesLoading;

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.hint}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SectionTitle style={styles.sectionGap}>Unscheduled Task Priority Bonus</SectionTitle>
      <Text style={styles.hint}>Added to the priority score of tasks with no scheduled time.</Text>
      <Field
        inputStyle={styles.input}
        value={String(bonus)}
        onChangeText={(t) => setBonus(parseFloat(t) || 0)}
        keyboardType="numeric"
      />

      <SectionTitle style={styles.sectionGap}>Values</SectionTitle>
      {values.length === 0 ? <Text style={styles.hint}>No values yet.</Text> : null}

      {values.map((value, idx) => (
        <GlassCard key={value.id} style={styles.card}>
          <View style={styles.row}>
            <View style={styles.reorderCol}>
              <Pressable
                onPress={() => moveValue(value.id, -1)}
                disabled={idx === 0}
                style={[styles.reorderBtn, idx === 0 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▲</Text>
              </Pressable>
              <Pressable
                onPress={() => moveValue(value.id, 1)}
                disabled={idx === values.length - 1}
                style={[styles.reorderBtn, idx === values.length - 1 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▼</Text>
              </Pressable>
            </View>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              value={value.name}
              onChangeText={(t) => updateValue(value.id, { name: t })}
              placeholder="Value name"
            />
            <ThemedSwitch value={value.isActive} onValueChange={(v) => updateValue(value.id, { isActive: v })} />
          </View>

          <Field
            inputStyle={[styles.input, styles.multiline]}
            value={value.description ?? ''}
            onChangeText={(t) => updateValue(value.id, { description: t })}
            placeholder="Description"
            multiline
          />

          <GlassCard style={styles.subCard}>
            <Text style={styles.label}>Criteria</Text>
            {value.criteria.map((criterion, cIdx) => (
              <View key={criterion.id} style={styles.row}>
                <View style={styles.reorderCol}>
                  <Pressable
                    onPress={() => moveCriterion(value, criterion.id, -1)}
                    disabled={cIdx === 0}
                    style={[styles.reorderBtn, cIdx === 0 && styles.reorderBtnDisabled]}
                  >
                    <Text style={styles.reorderText}>▲</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => moveCriterion(value, criterion.id, 1)}
                    disabled={cIdx === value.criteria.length - 1}
                    style={[styles.reorderBtn, cIdx === value.criteria.length - 1 && styles.reorderBtnDisabled]}
                  >
                    <Text style={styles.reorderText}>▼</Text>
                  </Pressable>
                </View>
                <Field style={styles.flex1}
                  inputStyle={[styles.input, styles.flex1]}
                  value={criterion.title}
                  onChangeText={(t) => updateCriterion(value, criterion.id, { title: t })}
                  placeholder="Criterion title"
                />
                <Field
                  inputStyle={[styles.input, styles.numInput]}
                  value={String(criterion.score)}
                  onChangeText={(t) => updateCriterion(value, criterion.id, { score: parseFloat(t) || 0 })}
                  keyboardType="numeric"
                  placeholder="Score"
                />
                <Pressable style={styles.deleteBtn} onPress={() => deleteCriterion(value, criterion.id)}>
                  <Text style={styles.deleteText}>✕</Text>
                </Pressable>
              </View>
            ))}
            <GoldButton label="+ Add Criterion" onPress={() => addCriterion(value)} style={styles.actionGap} />
          </GlassCard>

          <Pressable style={styles.dangerButton} onPress={() => deleteValue(value.id)}>
            <Text style={styles.dangerText}>Delete Value</Text>
          </Pressable>
        </GlassCard>
      ))}

      <GoldButton label="+ Add New Value" onPress={addValue} style={styles.actionGap} />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  container: { gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  card: {
    padding: 12,
    marginTop: 10,
    gap: 8,
  },
  subCard: {
    padding: 10,
    gap: 8,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  flex1: { flex: 1 },
  label: { fontSize: 12, color: t.ivorySoft, fontWeight: '700' },
  input: {
    marginTop: 8,
  },
  multiline: { minHeight: 60, textAlignVertical: 'top' },
  numInput: { width: 70 },
  reorderCol: { gap: 2 },
  reorderBtn: {
    width: 26,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  reorderBtnDisabled: { opacity: 0.25 },
  reorderText: { fontSize: 10, color: t.goldBright, fontWeight: '900' },
  deleteBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  deleteText: { color: t.lapsed, fontWeight: '900' },
  dangerButton: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 4,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  dangerText: { color: t.lapsed, fontWeight: '800' },
}),
);
